import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

import styles from './LinkContextMenu.module.css';

import { ensureHttps } from 'components/RteEditor/Plugins/LinkPlugin/helpers';
import { Icon } from 'components/Icon';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';
import { Button } from 'components/Buttons';

interface LinkContextMenuProps {
  url: string;
  onSave: (url: string) => void;
  onDelete: () => void;
  onClose: () => void;
}

export interface linkFormMethods {
  clearForm: () => void;
}

export const LinkContextMenu = forwardRef(({ url, onSave, onDelete, onClose }: LinkContextMenuProps, ref) => {
  const t = useTranslation();
  const [link, setLink] = useState(url);
  const [editable, setEditable] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setLink(url);
    setEditable(false);
    linkRef.current?.blur();
  };

  const handleSave = () => {
    onSave(link);
    reset();
  };

  const handleDelete = () => {
    onDelete();
    setLink('');
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePreview = () => {
    window.open(ensureHttps(url), '_blank');
  };

  useImperativeHandle(ref, () => ({
    clearForm: reset,
  }));

  useLayoutEffect(() => {
    setLink(url);

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'L') {
        event.preventDefault();
        setEditable(true);
        setTimeout(() => linkRef.current?.focus(), 0);
      }

      if (event.ctrlKey && event.shiftKey && event.code === 'Digit1') {
        event.preventDefault();
        handlePreview();
        setTimeout(() => linkRef.current?.focus(), 0);
      }

      if (event.ctrlKey && event.shiftKey && event.key === 'Q') {
        event.preventDefault();
        handleDelete();
        setTimeout(() => linkRef.current?.focus(), 0);
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <>
      <div className={styles['link-context-menu__wrapper']}>
        <input
          type="text"
          value={link}
          onChange={(event) => setLink(event.target.value)}
          className={styles['link-context-menu__input']}
          disabled={!editable}
          ref={linkRef}
        />
        {!editable && (
          <button
            type="button"
            className={styles['link-context-menu__button']}
            onClick={() => setEditable(true)}
            title={t.links.linkEdittip}
            aria-label={t.aria.labels.link}
            aria-keyshortcuts={t.aria.keyshortcuts.editlink}
          >
            <Icon name="Pencil" size={20} />
          </button>
        )}
        <button
          type="button"
          className={styles['link-context-menu__button']}
          onClick={handlePreview}
          title={t.links.linkTesttip}
          aria-label={t.aria.labels.link}
          aria-keyshortcuts={t.aria.keyshortcuts.testlink}
        >
          <Icon name="ArrowTopRightOnSquare" size={20} />
        </button>
        <button
          type="button"
          className={styles['link-context-menu__button']}
          onClick={handleDelete}
          title={t.links.linkDeletetip}
          aria-label={t.aria.labels.link}
          aria-keyshortcuts={t.aria.keyshortcuts.deletelink}
        >
          <Icon name="LinkSlash" size={20} />
        </button>
      </div>
      {editable && (
        <div className={styles['link-context-menu__actions']}>
          <Button variant={'secondary'} onClick={handleClose} aria-label={t.common.cancel} type="button">
            {t.common.cancel}
          </Button>
          <Button variant={'primary'} onClick={handleSave} aria-label={t.aria.labels.linkOkButton} type="button">
            {t.links.addLink}
          </Button>
        </div>
      )}
    </>
  );
});
